import { useMutation } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { DictationInput } from "../../components/DictationInput";
import { blobToBase64, useAudioRecorder } from "../../hooks/use-audio-recorder";
import { useSpeechRecognition } from "../../hooks/use-speech-recognition";
import { apiFetch } from "../../lib/api-client";
import { useSettingsStore } from "../../stores/settings-store";

export const Route = createFileRoute("/_app/dictation")({
  component: DictationPage,
});

type DictationResponse = {
  hiragana: string;
  /** 手話変換の候補絞り込み精度向上のためだけに使う漢字仮名交じり表記（DBには保存しない） */
  kanji: string;
  source: "text" | "audio";
};

/** AIを使わずローカル形態素解析だけで整形する「速報」（数百ms、精度はAIより粗い） */
const quickFormatText = (text: string): Promise<DictationResponse> =>
  apiFetch<DictationResponse>("/dictation/quick-format", {
    method: "POST",
    body: JSON.stringify({ text }),
  });

/** Gemini による最終整形（数秒かかるが精度が高い。速報を後から差し替える） */
const formatText = (text: string): Promise<DictationResponse> =>
  apiFetch<DictationResponse>("/dictation/format", {
    method: "POST",
    body: JSON.stringify({ text }),
  });

const transcribeAudio = async (blob: Blob): Promise<DictationResponse> => {
  const audio = await blobToBase64(blob);
  return apiFetch<DictationResponse>("/dictation/transcribe", {
    method: "POST",
    body: JSON.stringify({ audio, mimeType: blob.type || "audio/webm" }),
  });
};

const methodLabel = { text: "文字ベース", audio: "音声ベース" } as const;

type InputMode = "voice" | "keyboard";
const inputModeLabel = {
  voice: "🎤 マイク",
  keyboard: "⌨️ キーボード",
} as const;

function DictationPage() {
  const method = useSettingsStore((s) => s.dictationMethod);
  const speech = useSpeechRecognition();
  const recorder = useAudioRecorder();
  const [inputMode, setInputMode] = useState<InputMode>("voice");
  // 書き起こす文。マイクの認識結果が入り、キーボードで直せる
  const [draft, setDraft] = useState("");

  const quickMutation = useMutation({ mutationFn: quickFormatText });
  const formatMutation = useMutation({ mutationFn: formatText });
  const transcribeMutation = useMutation({ mutationFn: transcribeAudio });
  const activeMutation =
    method === "text" ? formatMutation : transcribeMutation;

  const recording = method === "text" ? speech.listening : recorder.recording;
  const supported = method === "text" ? speech.supported : recorder.supported;
  const error = method === "text" ? speech.error : recorder.error;
  const canConvert =
    !recording &&
    (method === "text" ? draft.trim() !== "" : recorder.audioBlob !== null);

  const onToggleRecording = async () => {
    if (recording) {
      if (method === "text") speech.stop();
      else recorder.stop();
      return;
    }
    quickMutation.reset();
    formatMutation.reset();
    transcribeMutation.reset();
    if (method === "text") {
      setDraft("");
      speech.reset();
      speech.start();
    } else {
      recorder.reset();
      await recorder.start();
    }
  };

  const onConvert = () => {
    if (method === "text" && draft.trim()) {
      quickMutation.mutate(draft);
      formatMutation.mutate(draft);
    } else if (method === "audio" && recorder.audioBlob) {
      transcribeMutation.mutate(recorder.audioBlob);
    }
  };

  // 録音停止と同時に自動で速報(quick-format)とAI変換(format)を並行キックする。
  // 速報が先に表示され、AIの結果が届き次第差し替わる。
  const wasListeningRef = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: mutate は ref ガードで一度しか発火しないため意図的に除外
  useEffect(() => {
    if (
      method === "text" &&
      wasListeningRef.current &&
      !speech.listening &&
      speech.transcript.trim() !== ""
    ) {
      setDraft(speech.transcript);
      quickMutation.mutate(speech.transcript);
      formatMutation.mutate(speech.transcript);
    }
    wasListeningRef.current = speech.listening;
  }, [method, speech.listening, speech.transcript]);

  const onChangeInputMode = (mode: InputMode) => {
    if (mode === "keyboard" && speech.listening) speech.stop();
    setInputMode(mode);
  };

  // 結果が出た後や、変換の返事を待っている間に文を直したら、
  // 古い文の結果（と「手話で見る」）が表示されないよう変換を捨てる
  const onChangeDraft = (value: string) => {
    if (quickMutation.status !== "idle" || formatMutation.status !== "idle") {
      quickMutation.reset();
      formatMutation.reset();
    }
    setDraft(value);
  };

  const displayResult = formatMutation.data ?? quickMutation.data;
  const isPreview =
    method === "text" && !formatMutation.data && !!quickMutation.data;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">書き起こし</h1>
        <p className="text-sm text-neutral-600 mt-1">
          話したことばを、ひらがなで文節ごとに書き起こします
        </p>
        <p className="text-xs text-neutral-400 mt-1">
          方式: {methodLabel[method]}（
          <Link to="/settings" className="underline hover:text-neutral-600">
            設定で変更
          </Link>
          ）
        </p>
      </div>

      {!supported && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-700">
          このブラウザはこの方式に対応していません（設定でもう一方の方式を試してください）
        </div>
      )}

      {supported && (
        <div className="space-y-4">
          {method === "text" && (
            <div className="grid grid-cols-2 gap-2">
              {(["voice", "keyboard"] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  aria-pressed={inputMode === mode}
                  onClick={() => onChangeInputMode(mode)}
                  className={`rounded-xl border py-2 text-sm font-semibold ${
                    inputMode === mode
                      ? "border-neutral-900 bg-neutral-900 text-white"
                      : "border-neutral-300 bg-white text-neutral-700"
                  }`}
                >
                  {inputModeLabel[mode]}
                </button>
              ))}
            </div>
          )}

          {(method === "audio" || inputMode === "voice") && (
            <button
              type="button"
              onClick={onToggleRecording}
              className={`w-full rounded-xl py-4 text-white text-lg font-semibold transition-colors ${
                recording
                  ? "bg-red-500 hover:bg-red-600"
                  : "bg-neutral-900 hover:bg-neutral-700"
              }`}
            >
              {recording ? "■ とめる" : "🎤 はなす"}
            </button>
          )}

          {method === "text" && speech.interim && (
            <p className="text-sm text-neutral-500">認識中: {speech.interim}</p>
          )}
          {method === "text" && (inputMode === "keyboard" || draft) && (
            <DictationInput
              value={draft}
              onChange={onChangeDraft}
              disabled={recording}
            />
          )}

          {error && (
            <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-700">
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={onConvert}
            disabled={!canConvert}
            className="w-full rounded-xl bg-neutral-900 py-3 text-white font-semibold disabled:opacity-40 hover:bg-neutral-700 transition-colors"
          >
            {activeMutation.isPending ? "変換中..." : "書き起こす"}
          </button>
        </div>
      )}

      {activeMutation.isError && (
        <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-700">
          {activeMutation.error.message}
        </div>
      )}

      {method === "text" && displayResult && (
        <div className="rounded-2xl border border-neutral-200 bg-white p-6">
          <p className="text-2xl font-bold leading-relaxed break-words">
            {displayResult.hiragana}
          </p>
          {isPreview && (
            <p className="mt-2 text-xs text-neutral-400">速報（AIで確認中…）</p>
          )}
        </div>
      )}
      {method === "text" && displayResult && (
        <Link
          to="/dictation/sign"
          search={{
            text: displayResult.hiragana,
            kanjiText: displayResult.kanji,
          }}
          className="block w-full rounded-xl border border-neutral-300 py-3 text-center font-semibold hover:bg-neutral-50 transition-colors"
        >
          🤟 手話で見る
        </Link>
      )}
      {method === "audio" && transcribeMutation.data && (
        <div className="rounded-2xl border border-neutral-200 bg-white p-6">
          <p className="text-2xl font-bold leading-relaxed break-words">
            {transcribeMutation.data.hiragana}
          </p>
        </div>
      )}
      {method === "audio" && transcribeMutation.data && (
        <Link
          to="/dictation/sign"
          search={{
            text: transcribeMutation.data.hiragana,
            kanjiText: transcribeMutation.data.kanji,
          }}
          className="block w-full rounded-xl border border-neutral-300 py-3 text-center font-semibold hover:bg-neutral-50 transition-colors"
        >
          🤟 手話で見る
        </Link>
      )}
    </div>
  );
}
